/**
 * Accredited schools feed for public/schoolShowcase (corporate ifip.group site).
 * Rows use source: "accredited"; Flagship admin uses source: "curated".
 * Keep eligibility + row shape aligned with functions/schoolShowcaseSync.js.
 */

import { Timestamp } from 'firebase/firestore';

export const PUBLIC_SHOWCASE_DOC_ID = 'schoolShowcase';
export const SHOWCASE_SCHEMA_VERSION = 2;

const COUNTRY_ALIASES = {
  uk: 'United Kingdom',
  'u.k.': 'United Kingdom',
  'u.k': 'United Kingdom',
  gb: 'United Kingdom',
  gbr: 'United Kingdom',
  'great britain': 'United Kingdom',
  england: 'United Kingdom',
  scotland: 'United Kingdom',
  wales: 'United Kingdom',
  'northern ireland': 'United Kingdom',
  usa: 'United States',
  us: 'United States',
  'u.s.': 'United States',
  'u.s.a.': 'United States',
  'united states of america': 'United States',
  uae: 'United Arab Emirates',
  emirates: 'United Arab Emirates',
};

export function tsToDate(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') return ts.toDate();
  if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
  if (ts instanceof Date) return ts;
  const parsed = new Date(ts);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function isGlobalAccreditationLevel(level) {
  const raw = (level || '').toString().toLowerCase();
  return raw === 'accredited' || raw === 'global';
}

export function normalizeShowcaseCountry(raw) {
  const trimmed = String(raw || '').trim();
  if (!trimmed) return '';

  if (trimmed.includes('|')) {
    const parts = trimmed.split('|').map((s) => s.trim()).filter(Boolean);
    const english = parts[parts.length - 1] || parts[0];
    const alias = COUNTRY_ALIASES[english.toLowerCase()];
    return alias || english;
  }

  const alias = COUNTRY_ALIASES[trimmed.toLowerCase()];
  if (alias) return alias;

  return trimmed
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      if (word.length <= 3 && word === word.toUpperCase()) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

export function resolveShowcaseCity(school) {
  const direct = String(
    school?.answers?.officialAddressCity || school?.answers?.city || ''
  ).trim();
  if (direct && !/^\d+$/.test(direct)) return direct;

  const address = String(school?.answers?.officialAddress || '').trim();
  if (!address) return '';

  const lines = address
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length >= 2) {
    const candidate = lines[lines.length - 2];
    if (candidate && !/^\d+$/.test(candidate)) return candidate;
  }
  return '';
}

export function mapAccreditationLevelForShowcase(accreditationLevel) {
  if (isGlobalAccreditationLevel(accreditationLevel)) {
    return { level: 'accredited', levelLabel: 'Global' };
  }
  if ((accreditationLevel || '').toString().toLowerCase() === 'certified') {
    return { level: 'certified', levelLabel: 'Certified' };
  }
  return { level: 'foundation', levelLabel: 'Foundation' };
}

export function getAccreditedIssuedAt(school, latestArchive) {
  return (
    tsToDate(latestArchive?.meta?.publishedAt) ||
    tsToDate(latestArchive?.archivedAt) ||
    tsToDate(school?.finalReportIssuedAt) ||
    null
  );
}

export function getAccreditedExpiryDate(issuedAt) {
  if (!issuedAt) return null;
  const expiry = new Date(issuedAt);
  expiry.setFullYear(expiry.getFullYear() + 2);
  return expiry;
}

export function hasIssuedAccreditationReport(school, latestArchive) {
  if (latestArchive) return true;
  return !!tsToDate(school?.finalReportIssuedAt);
}

/**
 * @returns {{ ok: boolean, reason?: string, issuedAt?: Date|null, expiryAt?: Date|null, isExpired?: boolean }}
 */
export function evaluateAccreditedShowcaseEligibility(school, latestArchive, { allowExpired = false } = {}) {
  if (!school) {
    return { ok: false, reason: 'School record not found.' };
  }
  const status = String(school.status || 'active').toLowerCase();
  if (status !== 'active') {
    return { ok: false, reason: 'School must be active.' };
  }
  if (school.workflowStep !== 'finalised') {
    return { ok: false, reason: 'School must have a finalised accreditation cycle.' };
  }

  if (!hasIssuedAccreditationReport(school, latestArchive)) {
    return { ok: false, reason: 'No issued report found in archive.' };
  }

  const issuedAt = getAccreditedIssuedAt(school, latestArchive);
  const expiryAt = getAccreditedExpiryDate(issuedAt);
  const isExpired = expiryAt ? Date.now() > expiryAt.getTime() : false;
  if (isExpired && !allowExpired) {
    return {
      ok: false,
      reason: 'Accreditation has passed its 24-month validity window.',
      issuedAt,
      expiryAt,
      isExpired: true,
    };
  }

  const name = String(school?.officialName || school?.answers?.officialName || '').trim();
  const country = normalizeShowcaseCountry(school?.answers?.officialAddressCountry);
  if (!name) {
    return { ok: false, reason: 'School name is required before publishing.' };
  }
  if (!country) {
    return { ok: false, reason: 'School country is required before publishing.' };
  }

  return { ok: true, issuedAt, expiryAt, isExpired };
}

function stripInstitutionalMaturityFromExecutiveSummary(text) {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/^INSTITUTIONAL MATURITY SCORE:\s*[\d.]+\/5\.00\s*(\n+|$)/i, '')
    .replace(/^Institutional Maturity Score:\s*[\d.]+\/5\.00\s*(\n+|$)/i, '')
    .trim();
}

/** Pen portrait for hub export only when school opted in; listing/logo/tier are unaffected. */
function resolveShowcaseExecutiveSummary(school, latestArchive) {
  if (school?.publicProfile?.publishExecutiveSummary !== true) return null;
  const raw = latestArchive?.institutionalBrief?.executiveSummary || '';
  const text = stripInstitutionalMaturityFromExecutiveSummary(String(raw));
  return text || null;
}

export function buildAccreditedShowcaseEntry(schoolId, school, latestArchive) {
  const issuedAt = getAccreditedIssuedAt(school, latestArchive);
  const expiryAt = getAccreditedExpiryDate(issuedAt);
  const issuedMs = issuedAt ? issuedAt.getTime() : 0;
  const tier = mapAccreditationLevelForShowcase(
    latestArchive?.accreditationLevel || school?.accreditationLevel
  );

  return {
    id: String(schoolId),
    schoolId: String(schoolId),
    name: String(school?.officialName || school?.answers?.officialName || '').trim(),
    country: normalizeShowcaseCountry(school?.answers?.officialAddressCountry),
    city: resolveShowcaseCity(school),
    level: tier.level,
    levelLabel: tier.levelLabel,
    logoUrl: school?.portalLogo?.publicUrl || null,
    executiveSummary: resolveShowcaseExecutiveSummary(school, latestArchive),
    sortOrder: issuedMs ? -issuedMs : 0,
    source: 'accredited',
    issuedAt: issuedAt ? issuedAt.toISOString() : null,
    expiresAt: expiryAt ? expiryAt.toISOString() : null,
    _expiryAtDate: expiryAt,
  };
}

/** Strip internal fields and write Firestore timestamp for hub contract. */
export function toFirestoreAccreditedRow(entry) {
  const { _expiryAtDate, ...rest } = entry;
  return {
    ...rest,
    accreditationExpiresAt: _expiryAtDate ? Timestamp.fromDate(_expiryAtDate) : null,
  };
}

export function splitShowcaseRows(data) {
  const schools = Array.isArray(data?.schools) ? data.schools : [];
  const flagshipSchools = Array.isArray(data?.flagshipSchools)
    ? data.flagshipSchools
    : schools.filter((s) => s?.source === 'curated');
  const accreditedSchools = Array.isArray(data?.accreditedSchools)
    ? data.accreditedSchools
    : schools.filter((s) => s?.source === 'accredited');
  return { flagshipSchools, accreditedSchools };
}

export function mergeShowcaseSchools(flagshipSchools, accreditedSchools) {
  const flagship = [...(flagshipSchools || [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );
  const accredited = [...(accreditedSchools || [])].sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );
  return [...flagship, ...accredited];
}

export function upsertAccreditedRow(accreditedSchools, entry) {
  const id = String(entry.id || entry.schoolId);
  const next = (accreditedSchools || []).filter(
    (r) => String(r.id) !== id && String(r.schoolId || r.id) !== id
  );
  next.push(entry);
  return next;
}

export function removeAccreditedRow(accreditedSchools, schoolId) {
  const id = String(schoolId);
  return (accreditedSchools || []).filter(
    (r) => String(r.id) !== id && String(r.schoolId || r.id) !== id
  );
}
